/**
 * Books Service - API calls for books/library management
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que le livre/demande/transaction visé
 * appartient bien à l'appelant. Il lui faut donc une identité prouvée : le
 * JWT signé au login, et non le `peopleId`/`ownerId`/`requesterId`/`senderId`
 * que le corps de la requête déclarait jusqu'ici — ces derniers venaient du
 * client, et se remplaçaient.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

// Normalize book data from ColdFusion API (BIT fields come as "0"/"1" strings, keys lowercase)
const normalizeBook = (book) => ({
    ...book,
    // Champs booléens BIT (arrivent comme "0"/"1" strings ou 0/1)
    forSale: Boolean(parseInt(book.forSale ?? book.forsale ?? 0)),
    forRent: Boolean(parseInt(book.forRent ?? book.forrent ?? 0)),
    forLend: Boolean(parseInt(book.forLend ?? book.forlend ?? 0)),
    forGive: Boolean(parseInt(book.forGive ?? book.forgive ?? 0)),
    isLocalImage: Boolean(parseInt(book.isLocalImage ?? book.islocalimage ?? 0)),
    // Champs numériques
    salePrice: parseFloat(book.salePrice ?? book.saleprice ?? 0) || 0,
    rentPricePerWeek: parseFloat(book.rentPricePerWeek ?? book.rentpriceperweek ?? 0) || 0,
    pageCount: parseInt(book.pageCount ?? book.pagecount ?? 0) || 0,
    // Champs texte camelCase
    imageUrl: book.imageUrl ?? book.imageurl ?? '',
    googleBooksId: book.googleBooksId ?? book.googlebooksid ?? '',
    publishedDate: book.publishedDate ?? book.publisheddate ?? '',
    ownerName: book.ownerName ?? book.ownername ?? '',
    ownerCity: book.ownerCity ?? book.ownercity ?? '',
    expectedReturnDate: book.expectedReturnDate ?? book.expectedreturndate ?? null,
    isActive: Boolean(parseInt(book.isActive ?? book.isactive ?? 1)),
});

// Normalize request data from ColdFusion API
const normalizeRequest = (req) => ({
    ...req,
    bookImageUrl: req.bookImageUrl ?? req.bookImage ?? req.BOOKIMAGEURL ?? req.BOOKIMAGE ?? '',
    isLocalImage: Boolean(parseInt(req.isLocalImage ?? req.islocalimage ?? 0)),
    proposedPrice: parseFloat(req.proposedPrice ?? req.estimatedPrice ?? req.PROPOSEDPRICE ?? req.ESTIMATEDPRICE ?? 0) || 0,
    numberOfWeeks: parseInt(req.numberOfWeeks ?? req.numberofweeks ?? req.NUMBEROFWEEKS ?? 0) || 0,
    requesterCity: req.requesterCity ?? req.requestercity ?? req.REQUESTERCITY ?? '',
    requesterName: req.requesterName ?? req.requestername ?? req.REQUESTERNAME ?? '',
    bookTitle: req.bookTitle ?? req.booktitle ?? req.BOOKTITLE ?? '',
    ownerName: req.ownerName ?? req.ownername ?? req.OWNERNAME ?? '',
    createdAt: req.createdAt ?? req.createdat ?? req.CREATEDAT ?? '',
    respondedAt: req.respondedAt ?? req.respondedat ?? req.RESPONDEDAT ?? null,
    status: req.status ?? req.STATUS ?? 'pending',
    requestType: req.requestType ?? req.requesttype ?? req.REQUESTTYPE ?? '',
    message: req.message ?? req.MESSAGE ?? '',
});

// Normalize transaction data from ColdFusion API
const normalizeTransaction = (tx) => {
    const userRole = tx.userRole ?? tx.userrole ?? tx.USERROLE ?? 'holder';
    const isOwner = userRole === 'owner';
    const otherPartyName = isOwner
        ? (tx.holderName ?? tx.holdername ?? tx.HOLDERNAME ?? '')
        : (tx.ownerName ?? tx.ownername ?? tx.OWNERNAME ?? '');
    return {
        ...tx,
        bookImageUrl: tx.bookImageUrl ?? tx.bookImage ?? tx.BOOKIMAGEURL ?? tx.BOOKIMAGE ?? '',
        isLocalImage: Boolean(parseInt(tx.isLocalImage ?? tx.islocalimage ?? 0)),
        price: parseFloat(tx.price ?? tx.agreedPrice ?? tx.agreedprice ?? tx.AGREEDPRICE ?? 0) || 0,
        expectedEndDate: tx.expectedEndDate ?? tx.expectedReturnDate ?? tx.expectedreturndate ?? tx.EXPECTEDRETURNDATE ?? null,
        actualEndDate: tx.actualEndDate ?? tx.actualReturnDate ?? tx.actualreturndate ?? tx.ACTUALRETURNDATE ?? null,
        bookTitle: tx.bookTitle ?? tx.booktitle ?? tx.BOOKTITLE ?? '',
        transactionType: tx.transactionType ?? tx.transactiontype ?? tx.TRANSACTIONTYPE ?? '',
        status: tx.status ?? tx.STATUS ?? 'pending_handover',
        userRole,
        isOwner,
        otherPartyName,
        otherPartyCity: tx.otherPartyCity ?? tx.otherpartycity ?? tx.OTHERPARTYCITY ?? '',
        numberOfWeeks: parseInt(tx.numberOfWeeks ?? tx.numberofweeks ?? tx.NUMBEROFWEEKS ?? 0) || 0,
        startDate: tx.startDate ?? tx.startdate ?? tx.STARTDATE ?? null,
    };
};

// ============ BOOKS CRUD ============

/**
 * Get public books catalog
 * @param {object} filters - Filters (status, availabilityType, keyword, minPrice, maxPrice)
 * @param {number} limit - Max results
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Array>} List of books
 */
export const getPublicBooks = async (filters = {}, limit = 50, offset = 0) => {
    try {
        const params = new URLSearchParams({ limit, offset });

        if (filters.status) params.append('status', filters.status);
        if (filters.availabilityType) params.append('availabilityType', filters.availabilityType);
        if (filters.keyword) params.append('keyword', filters.keyword);
        if (filters.minPrice) params.append('minPrice', filters.minPrice);
        if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);

        const response = await fetch(`${API_BASE}/books?${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const books = data.data ?? data.DATA ?? [];
        return success ? books.map(normalizeBook) : [];
    } catch (error) {
        console.error('Error fetching public books:', error);
        return [];
    }
};

/**
 * Get my books
 * @param {string} peopleId - User UUID
 * @param {number} limit - Max results
 * @param {number} offset - Offset
 * @returns {Promise<Array>} List of books
 */
export const getMyBooks = async (peopleId, limit = 100, offset = 0) => {
    try {
        const params = new URLSearchParams({ peopleId, limit, offset });
        const response = await fetch(`${API_BASE}/books/mine?${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const books = data.data ?? data.DATA ?? [];
        return success ? books.map(normalizeBook) : [];
    } catch (error) {
        console.error('Error fetching my books:', error);
        return [];
    }
};

/**
 * Get book by ID
 * @param {number} bookId - Book ID
 * @returns {Promise<object|null>} Book data or null
 */
export const getBookById = async (bookId) => {
    try {
        const response = await fetch(`${API_BASE}/books/${bookId}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const book = data.data ?? data.DATA;
        return success ? normalizeBook(book) : null;
    } catch (error) {
        console.error('Error fetching book:', error);
        return null;
    }
};

/**
 * Create a new book
 * @param {object} bookData - Book data
 * @returns {Promise<object>} Result with bookId
 */
export const createBook = async (bookData) => {
    try {
        const response = await fetch(`${API_BASE}/books`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(bookData)
        });
        return await response.json();
    } catch (error) {
        console.error('Error creating book:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a book
 * @param {number} bookId - Book ID
 * @param {object} bookData - Updated data (must include peopleId)
 * @returns {Promise<object>} Result
 */
export const updateBook = async (bookId, bookData) => {
    try {
        const response = await fetch(`${API_BASE}/books/${bookId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(bookData)
        });
        return await response.json();
    } catch (error) {
        console.error('Error updating book:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a book
 * @param {number} bookId - Book ID
 * @param {string} peopleId - Owner UUID
 * @returns {Promise<object>} Result
 */
export const deleteBook = async (bookId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE}/books/${bookId}?peopleId=${peopleId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        return await response.json();
    } catch (error) {
        console.error('Error deleting book:', error);
        return { success: false, message: error.message };
    }
};

// ============ GOOGLE BOOKS SEARCH ============

/**
 * Search books via Google Books API
 * @param {string} query - Search query
 * @param {number} maxResults - Max results (default 10)
 * @returns {Promise<Array>} List of books from Google
 */
export const searchGoogleBooks = async (query, maxResults = 10, lang = 'fr') => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const params = new URLSearchParams({
        q: query,
        maxResults: String(maxResults),
        langRestrict: lang,
        ...(apiKey ? { key: apiKey } : {})
    });
    const url = `https://www.googleapis.com/books/v1/volumes?${params}`;

    const MAX_RETRIES = 3;
    const BASE_DELAY = 400;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            if (attempt > 0) {
                await new Promise(r => setTimeout(r, BASE_DELAY * Math.pow(2, attempt - 1)));
            }
            const response = await fetch(url);
            if (response.status === 503 || response.status === 429) {
                if (attempt < MAX_RETRIES - 1) continue;
                return [];
            }
            const data = await response.json();
            const items = data.items || [];
            return items.map(item => {
                const info = item.volumeInfo || {};
                const imageLinks = info.imageLinks || {};
                return {
                    googleBooksId: item.id,
                    title: info.title || '',
                    authors: (info.authors || []).join(', '),
                    publisher: info.publisher || '',
                    publishedDate: info.publishedDate || '',
                    isbn10: (info.industryIdentifiers || []).find(i => i.type === 'ISBN_10')?.identifier || '',
                    isbn13: (info.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || '',
                    description: info.description || '',
                    pageCount: info.pageCount || null,
                    categories: (info.categories || []).join(', '),
                    language: info.language || '',
                    imageUrl: (imageLinks.thumbnail || imageLinks.smallThumbnail || '').replace('http://', 'https://'),
                };
            });
        } catch (error) {
            if (attempt === MAX_RETRIES - 1) {
                console.error('Error searching Google Books:', error);
                return [];
            }
        }
    }
    return [];
};

// ============ BOOK REQUESTS ============

/**
 * Create a request for a book
 * @param {number} bookId - Book ID
 * @param {object} requestData - Request data (requesterId, requestType, message, numberOfWeeks)
 * @returns {Promise<object>} Result with requestId
 */
export const createBookRequest = async (bookId, requestData) => {
    try {
        const response = await fetch(`${API_BASE}/books/${bookId}/request`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(requestData)
        });
        return await response.json();
    } catch (error) {
        console.error('Error creating book request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get requests received (as owner)
 * @param {string} peopleId - Owner UUID
 * @param {string} status - Filter by status (optional)
 * @returns {Promise<Array>} List of requests
 */
export const getRequestsReceived = async (peopleId, status = '') => {
    try {
        const params = new URLSearchParams({ peopleId });
        if (status) params.append('status', status);

        const response = await fetch(`${API_BASE}/books/requests/received?${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const requests = data.data ?? data.DATA ?? [];
        return success ? requests.map(normalizeRequest) : [];
    } catch (error) {
        console.error('Error fetching received requests:', error);
        return [];
    }
};

/**
 * Get requests sent (as requester)
 * @param {string} peopleId - Requester UUID
 * @param {string} status - Filter by status (optional)
 * @returns {Promise<Array>} List of requests
 */
export const getRequestsSent = async (peopleId, status = '') => {
    try {
        const params = new URLSearchParams({ peopleId });
        if (status) params.append('status', status);

        const response = await fetch(`${API_BASE}/books/requests/sent?${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const requests = data.data ?? data.DATA ?? [];
        return success ? requests.map(normalizeRequest) : [];
    } catch (error) {
        console.error('Error fetching sent requests:', error);
        return [];
    }
};

/**
 * Accept a book request
 * @param {number} requestId - Request ID
 * @param {string} ownerId - Owner UUID
 * @param {string} responseMessage - Optional response message
 * @param {string} expectedReturnDate - Expected return date (for rent/lend)
 * @returns {Promise<object>} Result with transactionId
 */
export const acceptBookRequest = async (requestId, ownerId, responseMessage = '', expectedReturnDate = '') => {
    try {
        const response = await fetch(`${API_BASE}/books/requests/${requestId}/accept`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify({ ownerId, responseMessage, expectedReturnDate })
        });
        return await response.json();
    } catch (error) {
        console.error('Error accepting request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Decline a book request
 * @param {number} requestId - Request ID
 * @param {string} ownerId - Owner UUID
 * @param {string} responseMessage - Optional decline reason
 * @returns {Promise<object>} Result
 */
export const declineBookRequest = async (requestId, ownerId, responseMessage = '') => {
    try {
        const response = await fetch(`${API_BASE}/books/requests/${requestId}/decline`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify({ ownerId, responseMessage })
        });
        return await response.json();
    } catch (error) {
        console.error('Error declining request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Cancel a book request (by requester)
 */
export const cancelBookRequest = async (requestId, requesterId) => {
    try {
        const response = await fetch(`${API_BASE}/books/requests/${requestId}/cancel`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify({ requesterId })
        });
        return await response.json();
    } catch (error) {
        console.error('Error cancelling book request:', error);
        return { success: false, message: error.message };
    }
};

// ============ TRANSACTIONS ============

/**
 * Get transactions
 * @param {string} peopleId - User UUID
 * @param {string} role - Filter by role ('owner' or 'holder')
 * @param {string} status - Filter by status
 * @returns {Promise<Array>} List of transactions
 */
export const getTransactions = async (peopleId, role = '', status = '') => {
    try {
        const params = new URLSearchParams({ peopleId });
        if (role) params.append('role', role);
        if (status) params.append('status', status);

        const response = await fetch(`${API_BASE}/books/transactions?${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const transactions = data.data ?? data.DATA ?? [];
        return success ? transactions.map(normalizeTransaction) : [];
    } catch (error) {
        console.error('Error fetching transactions:', error);
        return [];
    }
};

/**
 * Confirm book handover
 * @param {number} transactionId - Transaction ID
 * @param {string} ownerId - Owner UUID
 * @returns {Promise<object>} Result
 */
export const confirmHandover = async (transactionId, ownerId) => {
    try {
        const response = await fetch(`${API_BASE}/books/transactions/${transactionId}/confirm-handover`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ ownerId })
        });
        return await response.json();
    } catch (error) {
        console.error('Error confirming handover:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Mark book as returned
 * @param {number} transactionId - Transaction ID
 * @param {string} ownerId - Owner UUID
 * @returns {Promise<object>} Result
 */
export const markBookReturned = async (transactionId, ownerId) => {
    try {
        const response = await fetch(`${API_BASE}/books/transactions/${transactionId}/returned`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ ownerId })
        });
        return await response.json();
    } catch (error) {
        console.error('Error marking book returned:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Send a reminder for a transaction
 * @param {number} transactionId - Transaction ID
 * @param {string} senderId - Sender UUID
 * @returns {Promise<object>} Result
 */
export const sendTransactionReminder = async (transactionId, senderId) => {
    try {
        const response = await fetch(`${API_BASE}/books/transactions/${transactionId}/remind`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ senderId })
        });
        return await response.json();
    } catch (error) {
        console.error('Error sending reminder:', error);
        return { success: false, message: error.message };
    }
};

// Alias for sendTransactionReminder
export const sendStatusReminder = sendTransactionReminder;

// ============ HELPERS ============

/**
 * Get book condition label
 * @param {string} condition - Condition code
 * @param {string} lang - Language (fr, en)
 * @returns {string} Condition label
 */
export const getConditionLabel = (condition, lang = 'fr') => {
    const labels = {
        fr: {
            new: 'Neuf',
            like_new: 'Comme neuf',
            good: 'Bon état',
            acceptable: 'Acceptable',
            worn: 'Usé'
        },
        en: {
            new: 'New',
            like_new: 'Like new',
            good: 'Good',
            acceptable: 'Acceptable',
            worn: 'Worn'
        }
    };
    return labels[lang]?.[condition] || condition;
};

/**
 * Get book status label and color
 * @param {string} status - Status code
 * @param {string} expectedReturnDate - Expected return date
 * @param {string} lang - Language
 * @returns {object} { label, color, isOverdue }
 */
export const getStatusInfo = (status, expectedReturnDate = null, lang = 'fr') => {
    const now = new Date();
    const returnDate = expectedReturnDate ? new Date(expectedReturnDate) : null;
    const isOverdue = returnDate && returnDate < now && (status === 'rented' || status === 'lent');

    const labels = {
        fr: {
            available: 'Disponible',
            sold: 'Vendu',
            given: 'Donné',
            rented: 'Loué',
            lent: 'Prêté'
        },
        en: {
            available: 'Available',
            sold: 'Sold',
            given: 'Given away',
            rented: 'Rented',
            lent: 'Lent'
        }
    };

    let label = labels[lang]?.[status] || status;
    let color = 'green';

    if (status === 'sold' || status === 'given') {
        color = 'gray';
    } else if (status === 'rented' || status === 'lent') {
        if (returnDate) {
            const dateStr = returnDate.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
                day: 'numeric',
                month: 'short'
            });
            label += ` jusqu'au ${dateStr}`;
        }
        color = isOverdue ? 'red' : (status === 'rented' ? 'orange' : 'blue');
    }

    return { label, color, isOverdue };
};

/**
 * Get transaction status info
 * @param {string} status - Transaction status
 * @param {string} lang - Language
 * @returns {object} { label, color }
 */
export const getTransactionStatusInfo = (status, lang = 'fr') => {
    const info = {
        fr: {
            pending_handover: { label: 'En attente de remise', color: 'yellow' },
            active: { label: 'En cours', color: 'blue' },
            overdue: { label: 'En retard', color: 'red' },
            completed: { label: 'Terminé', color: 'green' },
            cancelled: { label: 'Annulé', color: 'gray' }
        },
        en: {
            pending_handover: { label: 'Pending handover', color: 'yellow' },
            active: { label: 'Active', color: 'blue' },
            overdue: { label: 'Overdue', color: 'red' },
            completed: { label: 'Completed', color: 'green' },
            cancelled: { label: 'Cancelled', color: 'gray' }
        }
    };

    return info[lang]?.[status] || { label: status, color: 'gray' };
};

/**
 * Get transaction status label (simple version)
 * @param {string} status - Transaction status
 * @returns {string} Label
 */
export const getTransactionStatusLabel = (status) => {
    const labels = {
        pending_handover: 'En attente de remise',
        active: 'En cours',
        overdue: 'En retard',
        completed: 'Terminé',
        cancelled: 'Annulé'
    };
    return labels[status] || status;
};

/**
 * Get transaction type label
 * @param {string} type - Transaction type (sale, rental, loan, gift)
 * @returns {string} Label
 */
export const getTransactionTypeLabel = (type) => {
    const labels = {
        sale: 'Vente',
        rental: 'Location',
        loan: 'Prêt',
        gift: 'Don'
    };
    return labels[type] || type;
};

/**
 * Get request type label
 * @param {string} requestType - Request type
 * @param {string} lang - Language
 * @returns {string} Label
 */
export const getRequestTypeLabel = (requestType, lang = 'fr') => {
    const labels = {
        fr: {
            buy: 'Acheter',
            rent: 'Louer',
            borrow: 'Emprunter',
            receive: 'Recevoir (don)'
        },
        en: {
            buy: 'Buy',
            rent: 'Rent',
            borrow: 'Borrow',
            receive: 'Receive (gift)'
        }
    };
    return labels[lang]?.[requestType] || requestType;
};

/**
 * Calculate rental price
 * @param {number} pricePerWeek - Price per week
 * @param {number} numberOfWeeks - Number of weeks
 * @returns {number} Total price
 */
export const calculateRentalPrice = (pricePerWeek, numberOfWeeks) => {
    return (pricePerWeek || 0) * (numberOfWeeks || 0);
};

// Alias for backward compatibility
export const getMyBookRequests = getRequestsSent;

/**
 * Get request status label
 * @param {string} status - Request status
 * @param {string} lang - Language
 * @returns {string} Label
 */
export const getRequestStatusLabel = (status, lang = 'fr') => {
    const labels = {
        fr: {
            pending: 'En attente',
            accepted: 'Acceptée',
            declined: 'Refusée',
            cancelled: 'Annulée'
        },
        en: {
            pending: 'Pending',
            accepted: 'Accepted',
            declined: 'Declined',
            cancelled: 'Cancelled'
        }
    };
    return labels[lang]?.[status] || status;
};

/**
 * Get book count
 * @param {string} peopleId - Optional user UUID to filter
 * @returns {Promise<number>} Book count
 */
export const getBookCount = async (peopleId = '') => {
    try {
        const params = peopleId ? `?peopleId=${peopleId}` : '';
        const response = await fetch(`${API_BASE}/books/count${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const count = data.count ?? data.COUNT ?? 0;
        return success ? count : 0;
    } catch (error) {
        console.error('Error fetching book count:', error);
        return 0;
    }
};
